import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Email as EmailIcon,
  DoneAll as DoneAllIcon,
  Send as SendIcon
} from '@mui/icons-material';

const EmailDetailView = ({ email, onBack, onNavigate }) => {


  if (!email) return null;

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const statusConfig = {
    sent: { 
      label: 'Sent', 
      color: '#ff9800', 
      icon: <SendIcon sx={{ fontSize: 14 }} /> 
    },
    opened: { 
      label: 'Opened', 
      color: '#4caf50', 
      icon: <DoneAllIcon sx={{ fontSize: 14 }} /> 
    },
    delivered: { 
      label: 'Delivered', 
      color: '#2196f3', 
      icon: <DoneAllIcon sx={{ fontSize: 14 }} /> 
    },
    failed: { 
      label: 'Failed', 
      color: '#f44336', 
      icon: <SendIcon sx={{ fontSize: 14 }} /> 
    },
    bounced: { 
      label: 'Bounced', 
      color: '#9e9e9e', 
      icon: <SendIcon sx={{ fontSize: 14 }} /> 
    }
  };

  const config = statusConfig[email.status] || statusConfig.sent;

  // Use the actual HTML content from the email prop, or fallback to text content
  const emailContent = email.htmlContent || email.textContent || 'No content available';

  return (
    <Box>
      {/* Top Header Bar with Title and Navigation */}
      <Paper sx={{ 
        p: 2, 
        mb: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={onBack}
            size="small"
            sx={{ 
              color: '#666',
              '&:hover': { bgcolor: '#f5f5f5' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            {email.subject}
          </Typography>
        </Box>
        
        {onNavigate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={() => onNavigate('prev')}
              size="small"
              sx={{ 
                bgcolor: '#f5f5f5',
                '&:hover': { bgcolor: '#e0e0e0' }
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <IconButton 
              onClick={() => onNavigate('next')}
              size="small"
              sx={{ 
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>

      {/* Email Header Section */}
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#e3f2fd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <EmailIcon sx={{ color: '#1976d2', fontSize: 20 }} />
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.5 }}>
                  {email.from || 'Site Inspections'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  To: {email.recipient}
                </Typography>
                {email.cc && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem', display: 'block' }}>
                    CC: {email.cc}
                  </Typography>
                )}
                {email.bcc && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem', display: 'block' }}>
                    BCC: {email.bcc}
                  </Typography>
                )}
              </Box>
              <Chip
                icon={config.icon}
                label={config.label}
                size="small"
                sx={{
                  bgcolor: 'transparent',
                  color: config.color,
                  border: `1px solid ${config.color}`,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  height: '22px',
                  '& .MuiChip-icon': {
                    color: config.color
                  }
                }}
              />
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
              Sent: {formatDateTime(email.sentDate)}
            </Typography>
            {email.openedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                Opened: {formatDateTime(email.openedAt)}
              </Typography>
            )}
            {email.deliveredAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                Delivered: {formatDateTime(email.deliveredAt)}
              </Typography>
            )}
            {email.clickedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                Clicked: {formatDateTime(email.clickedAt)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Additional Email Metadata */}
        {(email.messageId || email.priority || email.templateId) && (
          <Box sx={{ 
            mt: 2, 
            pt: 2, 
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2
          }}>
          
            {email.priority && (
              <Chip 
                label={`Priority: ${email.priority}`} 
                size="small" 
                variant="outlined"
                sx={{ height: '20px', fontSize: '0.7rem' }}
              />
            )}
         
          </Box>
        )}

      
      </Paper>

      {/* Email Content */}
      <Paper sx={{ 
        bgcolor: '#fafafa',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          bgcolor: 'white',
          minHeight: '500px',
          p: email.htmlContent ? 0 : 3
        }}>
          {email.htmlContent ? (
            <div dangerouslySetInnerHTML={{ __html: emailContent }} />
          ) : (
            <Typography 
              component="pre" 
              sx={{ 
                whiteSpace: 'pre-wrap', 
                fontFamily: 'Arial, sans-serif',
                fontSize: '0.9rem',
                lineHeight: 1.6
              }}
            >
              {emailContent}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Error Information (if any) */}
      {(email.failureReason || email.bounceReason) && (
        <Paper sx={{ p: 2, mt: 2, bgcolor: '#ffebee' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#c62828', mb: 1 }}>
            Delivery Issue
          </Typography>
          {email.failureReason && (
            <Typography variant="caption" sx={{ display: 'block', color: '#d32f2f' }}>
              Failure Reason: {email.failureReason}
            </Typography>
          )}
          {email.bounceReason && (
            <Typography variant="caption" sx={{ display: 'block', color: '#d32f2f' }}>
              Bounce Reason: {email.bounceReason}
            </Typography>
          )}
          {email.bouncedAt && (
            <Typography variant="caption" sx={{ display: 'block', color: '#d32f2f', mt: 1 }}>
              Bounced At: {formatDateTime(email.bouncedAt)}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default EmailDetailView;